import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, Button, CardView, CurrencyAmountText, Indicator, rem, Spacer, withAnimatedNavigationHeader, } from 'design-system';
import { Avatar } from 'design-system/avatar';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import SectionHeader from 'design-system/SectionHeader';
import Squircle from 'design-system/Squircle';
import StatusIcon from 'design-system/StatusIcon';
import Text from 'design-system/Text';
import { getProductTransfer } from 'features/invest/actions';
import { InfoModal } from 'features/invest/components/InfoModal';
import ProgressCard from 'features/invest/components/InvestOnboardingFunnel/ProgressCard';
import ProductTransferDetails from 'features/invest/components/ProductTransferDetails';
import { AccountTypeDisplayLong } from 'features/invest/constants';
import { selectActiveTradingAccountId, selectCachedProductTransfer } from 'features/invest/reducers/selectors';
import { formatDateInProgressTimeline } from 'features/invest/utils';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { assertUnreachable } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
const strings = {
    subtitle: {
        WITH_PREVIOUS_PROVIDER: 'Transfer request',
        ONBOARDING_ASSETS: 'Transfer request',
        REVIEWING_DOCUMENTATION: 'Transfer request',
        REJECTED: 'Transfer rejected',
        COMPLETED: 'Transfer completed',
        DELETED: 'Transfer request deleted',
    },
    transferRequested: 'Transfer requested',
    transferDetails: 'Transfer details',
    providerContacted: (provider) => `We’ve contacted ${provider}`,
    providerContactedDescription: (t) => `They’re preparing to transfer your ${AccountTypeDisplayLong[t]}.`,
    providerConfirmed: (provider) => `${provider} has confirmed the transfer request`,
    transferNotStarted: 'Transfer hasn’t started yet',
    transferInProgress: (t) => `Your ${AccountTypeDisplayLong[t]} transfer is in progress`,
    timeframeDescription: 'This usually takes 4–6 weeks, but can take longer.',
    transferDidNotGoThrough: 'Your transfer didn’t go through',
    transferRejectedDescription: (provider) => `${provider} couldn’t process the transfer request. Your money hasn’t moved and is still safely with your current provider.`,
    transferComplete: (t) => `Your ${AccountTypeDisplayLong[t]} transfer is complete`,
    transferCompleteDescription: (t) => `Your money has successfully arrived in your ${AccountTypeDisplayLong[t]}.`,
    transferDeleted: 'Transfer deleted',
    close: 'Close',
};
const getStatusIconState = (progress) => {
    switch (progress) {
        case 'COMPLETED':
            return 'done';
        case 'REJECTED':
        case 'DELETED':
            return 'cancelled-red';
        default:
            return 'in-progress';
    }
};
const InitCard = React.memo(({ requestTime, showDetails }) => {
    const styles = useStyles(styleSet);
    return (<ProgressCard lightBg completed noTextDecoration alwaysShowTimeline alwaysShowChildren title={strings.transferRequested} timeInfo={formatDateInProgressTimeline(requestTime)}>
      <Spacer h={8}/>
      <Button hug brandReversed square title={strings.transferDetails} onPress={showDetails} containerStyle={styles.toLeft}/>
    </ProgressCard>);
});
const ProviderCard = React.memo(({ accountType, providerName, status }) => {
    switch (status) {
        case 'REVIEWING_DOCUMENTATION':
        case 'WITH_PREVIOUS_PROVIDER':
            return (<ProgressCard lightBg isActiveStep completed={false} title={strings.providerContacted(providerName)}>
          <Text TextThin-14 Secondary>
            {strings.providerContactedDescription(accountType)}
          </Text>
        </ProgressCard>);
        case 'DELETED':
            return null;
        default:
            return <ProgressCard lightBg completed noTextDecoration title={strings.providerConfirmed(providerName)}/>;
    }
});
const FinalCard = React.memo(({ accountType, status, providerName, resultTime }) => {
    switch (status) {
        case 'REVIEWING_DOCUMENTATION':
        case 'WITH_PREVIOUS_PROVIDER':
            return (<ProgressCard lightBg lastItem completed={false} title={strings.transferNotStarted}>
          <Text TextThin-14 Secondary>
            {strings.timeframeDescription}
          </Text>
        </ProgressCard>);
        case 'ONBOARDING_ASSETS':
            return (<ProgressCard lightBg lastItem isActiveStep completed={false} title={strings.transferInProgress(accountType)}>
          <Text TextThin-14 Secondary>
            {strings.timeframeDescription}
          </Text>
        </ProgressCard>);
        case 'REJECTED':
            return (<ProgressCard lightBg lastItem isActiveStep isFailure completed={false} title={strings.transferDidNotGoThrough} timeInfo={formatDateInProgressTimeline(resultTime)}>
          <Text TextThin-14 Secondary>
            {strings.transferRejectedDescription(providerName)}
          </Text>
        </ProgressCard>);
        case 'COMPLETED':
            return (<ProgressCard lightBg lastItem completed noTextDecoration alwaysShowChildren alwaysShowTimeline title={strings.transferComplete(accountType)} timeInfo={formatDateInProgressTimeline(resultTime)}>
          <Text TextThin-14 Secondary>
            {strings.transferCompleteDescription(accountType)}
          </Text>
        </ProgressCard>);
        case 'DELETED':
            return <ProgressCard lightBg lastItem isActiveStep isFailure completed={false} title={strings.transferDeleted}/>;
        default:
            assertUnreachable(status);
            return null;
    }
});
function useTransferProgressInfo(params) {
    const dispatch = useAppDispatch();
    const id = params.productTransferId;
    const tradingAccountIdFromProps = params.accountId;
    const cached = useAppSelector((state) => selectCachedProductTransfer(state, id));
    const tradingAccountIdFromStore = useAppSelector(selectActiveTradingAccountId);
    const tradingAccountId = tradingAccountIdFromProps ?? tradingAccountIdFromStore;
    useEffect(() => {
        if (tradingAccountId) {
            dispatch(getProductTransfer(id, tradingAccountId));
        }
    }, [id, tradingAccountId]);
    return cached;
}
const ProductTransferProgressScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const transferDetails = useTransferProgressInfo(route.params);
    const providerName = transferDetails?.transferProvider?.shortName || transferDetails?.transferProvider?.officialName;
    const displayTransferAmount = transferDetails?.finalTransferValue ?? transferDetails?.userProvidedTransferValue;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => <NavigationCloseButton inWebHeader onPress={navigation.goBack} colorKey="primary"/>,
        });
    }, [colors.text.primary]);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING);
    const showTransferDetails = useCallback(() => {
        if (transferDetails && providerName) {
            InfoModal.show(strings.transferDetails, <View>
            <Spacer h={8}/>
            <SectionHeader>Transfer from</SectionHeader>
            <Spacer.H16 />
            <CardView noMargin style={styles.detailsModalCard}>
              <ProductTransferDetails providerName={providerName} type={transferDetails.previousProviderAccountType} accountType={transferDetails.accountType} providerLogoUrl={transferDetails.transferProvider.imgUrl} identification={transferDetails.previousProviderSortCode && transferDetails.previousProviderAccountNumber
                    ? {
                        previousProviderSortCode: transferDetails.previousProviderSortCode,
                        previousProviderAccountNumber: transferDetails.previousProviderAccountNumber,
                    }
                    : { accountReference: transferDetails.previousProviderAccountReference ?? '' }} balance={transferDetails.userProvidedTransferValue} hideBalance={transferDetails.providerStatus === 'COMPLETED'}/>
            </CardView>
          </View>);
        }
    }, [providerName, styles.detailsModalCard, transferDetails]);
    if (!transferDetails || !providerName || typeof displayTransferAmount !== 'number') {
        return (<AppView>
          <Indicator dark/>
        </AppView>);
    }
    const status = transferDetails.providerStatus || 'WITH_PREVIOUS_PROVIDER';
    return (<AppView>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator={false}>
          <Spacer h={24}/>
          <View style={styles.amountInfo}>
            <View>
              <View style={styles.iconContainer}>
                <Avatar imageUrl={transferDetails.transferProvider.imgUrl} name={providerName} color={colors.background.brandLight} fontColor={colors.text.textOnBrand} size={52} borderWidth={6} style={{ borderColor: colors.cards.onDark }}/>
              </View>
              <View style={styles.statusIconContainer}>
                <Squircle width={rem(24)} height={rem(24)} color={colors.cards.onDark}>
                  <StatusIcon state={getStatusIconState(status)} height={20} width={20}/>
                </Squircle>
              </View>
            </View>
            <Spacer h={24}/>
            <Text Text-14 Secondary>
              {strings.subtitle[status]}
            </Text>
            <CurrencyAmountText size="XL" value={{ amount: displayTransferAmount, currency: 'GBP' }}/>
          </View>
          <Spacer h={8}/>
          <InitCard requestTime={transferDetails.createdAt} showDetails={showTransferDetails}/>
          <ProviderCard providerName={providerName} accountType={transferDetails.accountType} status={status}/>
          <FinalCard status={status} providerName={providerName} accountType={transferDetails.accountType} resultTime={transferDetails.providerStatusUpdatedAt}/>
        </Animated.ScrollView>

        <Button floating title={strings.close} brand onPress={navigation.goBack}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default ProductTransferProgressScreen;
const styleSet = createStyleSheets((colors) => ({
    amountInfo: {
        alignItems: 'center',
    },
    toLeft: {
        alignSelf: 'flex-start',
    },
    iconContainer: {
        width: rem(64),
        height: rem(64),
        borderRadius: rem(64),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.cards.onDark,
    },
    statusIconContainer: {
        right: 0,
        bottom: 0,
        position: 'absolute',
    },
    detailsModalCard: {
        backgroundColor: colors.background.dark,
    },
}));
